import React from "react";
import { view } from "../../lib/vvm";
import { Layout } from "../../components/Layout/Layout";
import Icon, { IconSize } from "../../components/Icon/Icon";
import Button, { ButtonDesign } from "../../components/Button/Button";
import { ProductPhotoSlider } from "../../components/ProductPhotoSlider/ProductPhotoSlider";
import { Measures } from "../../types/entities";
import { formatTypeTag, normalizeTypeTags } from "../../utils/typeTags";
import {
  ProductsListPageViewModel,
  type ProductsListPageViewProps,
} from "./ProductsListPageViewModel";
import { VirtualizedChunks } from "../../components/VirtualizedChunks/VirtualizedChunks";
import styles from "./ProductsListPage.module.css";

function formatQuantity(value: number, measure: Measures): string {
  const safeValue = Number.isFinite(value) ? value : 1;

  if (measure === Measures.weight) {
    return `${safeValue} кг`;
  }

  if (measure === Measures.liters) {
    return `${safeValue} л`;
  }

  return `${safeValue} шт`;
}

interface ProductsListPageProps extends ProductsListPageViewProps {}

export const ProductsListPage: React.FC<ProductsListPageProps> = view(
  ProductsListPageViewModel,
)<ProductsListPageProps>(({
  viewModel: {
    pageTitle,
    summaryLabel,
    appliedFilterChips,
    items,
    error,
    hasMore,
    canAutoLoadMore,
    isLoadingInitial,
    isLoadingMore,
    refresh,
    loadMore,
    openProduct,
    openTypeTag,
    openMeasureFilter,
    openLocationFilter,
    resolveLocation,
  },
}) => {
  return (
    <Layout title={pageTitle}>
      <section className={styles.root}>
        <div className={styles.toolbar}>
          <div className={styles.titleBlock}>
            <strong className={styles.title}>{pageTitle}</strong>
            <span className={styles.subtitle}>{summaryLabel}</span>
          </div>
          <Button
            design={ButtonDesign.ghost}
            className={styles.refresh}
            onClick={() => void refresh()}
            disabled={isLoadingInitial || isLoadingMore}
          >
            обновить
          </Button>
        </div>

        <div className={styles.filters}>
          {appliedFilterChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={`${styles.filterChip} ${styles.filterChipButton}`}
              onClick={chip.onClick}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {error && (
          <div className={styles.error}>
            <span>{error}</span>
            <Button
              design={ButtonDesign.basic}
              className={styles.retry}
              onClick={() => void refresh()}
            >
              повторить
            </Button>
          </div>
        )}

        {isLoadingInitial ? (
          <div className={styles.stateCard}>
            <Icon
              name={"AddFile"}
              size={IconSize.large}
              className={styles.stateIcon}
              loading
            />
            <strong>Загружаем товары</strong>
            <span>Подготавливаем первую страницу списка</span>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.stateCard}>
            <Icon
              name={"AddFile"}
              size={IconSize.large}
              className={styles.stateIcon}
            />
            <strong>Товары не найдены</strong>
            <span>
              Попробуйте обновить список или изменить фильтр из статистики
            </span>
          </div>
        ) : (
          <VirtualizedChunks
            items={items}
            chunkSize={8}
            windowChunks={2}
            hasMore={canAutoLoadMore}
            isLoadingMore={isLoadingMore}
            className={styles.virtualList}
            contentClassName={styles.virtualContent}
            loadingLabel={"Загружаем следующую страницу..."}
            onLoadMore={() => void loadMore()}
            renderChunk={(chunkId, chunkItems) => (
              <ul
                key={chunkId}
                className={styles.list}
                aria-label={`Товары, блок ${chunkId + 1}`}
              >
                {chunkItems.map((item) => {
                  const typeTags = normalizeTypeTags(item.typeTags);
                  const quantity = formatQuantity(item.quantity, item.measure);
                  const itemName = item.name || "Без названия";

                  return (
                    <li key={item.id} className={styles.item}>
                      <article className={styles.card}>
                        <button
                          type="button"
                          className={styles.previewButton}
                          onClick={() => openProduct(item)}
                          aria-label={`Открыть товар ${itemName}`}
                        >
                          <ProductPhotoSlider
                            photo={item.photo}
                            photos={item.photos}
                            alt={itemName}
                            className={styles.previewSlider}
                            trackClassName={styles.previewTrack}
                            imageClassName={styles.previewImage}
                            emptyClassName={styles.previewEmpty}
                          />
                        </button>

                        <div className={styles.content}>
                          <button
                            type="button"
                            className={styles.titleButton}
                            onClick={() => openProduct(item)}
                          >
                            {itemName}
                          </button>
                          <span className={styles.qtyBadge}>{quantity}</span>
                          <p className={styles.description}>
                            {item.description?.trim() || "Без описания"}
                          </p>
                        </div>

                        <div className={styles.metaRow}>
                          {typeTags.length > 0 ? (
                            typeTags.map((tag) => (
                              <button
                                key={`${item.id}-tag-${tag}`}
                                type="button"
                                className={`${styles.metaTag} ${styles.metaTagButton}`}
                                onClick={() => openTypeTag(tag)}
                              >
                                {formatTypeTag(tag)}
                              </button>
                            ))
                          ) : (
                            <span className={styles.metaTag}>без тега</span>
                          )}
                          <button
                            type="button"
                            className={`${styles.metaTag} ${styles.metaTagButton}`}
                            onClick={() => openMeasureFilter(item.measure)}
                          >
                            мера: {item.measure}
                          </button>
                          <button
                            type="button"
                            className={`${styles.metaTag} ${styles.metaTagButton}`}
                            onClick={() => openLocationFilter(item)}
                          >
                            {resolveLocation(item)}
                          </button>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          />
        )}
        {!isLoadingInitial &&
          !isLoadingMore &&
          !hasMore &&
          items.length > 0 && (
            <p className={styles.loadMoreState}>Показаны все товары</p>
          )}
      </section>
    </Layout>
  );
});
